import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

interface CampaignModeStepProps {
  selectedMode: "regular" | "ai" | null;
  onSelectMode: (mode: "regular" | "ai") => void;
  onNext: () => void;
  onBack?: () => void;
  hasCompanyProfiles?: boolean;
  isAiEnabled?: boolean;
}

export function CampaignModeStep({
  selectedMode,
  onSelectMode,
  onNext,
  onBack,
  hasCompanyProfiles = false,
  isAiEnabled = true,
}: CampaignModeStepProps) {
  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold tracking-tight">
          How do you want to create your campaign?
        </h2>
        <p className="text-muted-foreground">
          Choose the approach that works best for you
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
        {/* Regular Mode Card */}
        <Card
          className={cn(
            "cursor-pointer transition-all hover:shadow-lg group",
            selectedMode === "regular"
              ? "ring-2 ring-primary shadow-lg"
              : "hover:border-primary/50"
          )}
          onClick={() => onSelectMode("regular")}
        >
          <CardContent className="p-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
              <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-3">Regular Mode</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Define your target audience and messages manually
            </p>
          </CardContent>
        </Card>

        {/* AI Mode Card */}
        {isAiEnabled && (
          <Card
            className={cn(
              "cursor-pointer transition-all hover:shadow-lg relative overflow-hidden group",
              selectedMode === "ai"
                ? "ring-2 ring-primary shadow-lg"
                : "hover:border-primary/50"
            )}
            onClick={() => onSelectMode("ai")}
          >
            {/* Recommended badge */}
            {hasCompanyProfiles && (
              <div className="absolute top-0 right-0">
                <div className="bg-gradient-to-r from-primary to-primary text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">
                  RECOMMENDED
                </div>
              </div>
            )}
            
            {/* Gradient border effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/10 pointer-events-none" />
            
            <CardContent className="p-8 text-center relative">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/20 flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-3">AI Mode</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Let AI define your campaign from your business DNA
              </p>
            </CardContent>
          </Card>
        )}

        {/* If AI is not enabled, show single centered card */}
        {!isAiEnabled && (
          <Card
            className="cursor-not-allowed opacity-50 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5 pointer-events-none" />
            <CardContent className="p-8 text-center relative">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-5">
                <Sparkles className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-lg mb-3 text-muted-foreground">AI Mode</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                AI features are not enabled for this workspace
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center max-w-2xl mx-auto pt-4">
        {onBack ? (
          <Button variant="outline" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        ) : (
          <div />
        )}
        <Button
          onClick={onNext}
          disabled={!selectedMode}
          className={cn(
            "gap-2",
            selectedMode === "ai" && "bg-gradient-to-r from-primary to-primary hover:from-primary hover:to-primary"
          )}
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
